/**
 * Cross-subdomain Supabase auth storage (single source of truth).
 *
 * Supabase-js defaults to localStorage, which is scoped per-origin and
 * therefore NOT shared between subdomains. Every ReHub app lives on
 * *.rehub.studio and needs a single session across all of them.
 *
 * This adapter mirrors the session into a cookie scoped to `.rehub.studio`
 * (chunked if it exceeds the ~4 KB cookie limit) in addition to localStorage.
 * On non-production hosts (localhost, Vercel preview URLs, etc.) it falls back
 * to localStorage-only.
 *
 * IMPORTANT: this module is intentionally side-effect-free (no env access, no
 * client creation) so every app can import it without spinning up a second
 * Supabase client. Each app keeps its own `createClient(...)` call and just
 * passes `storage: createCrossSubdomainAuthStorage()`.
 *
 * Why the chunking is by ENCODED length: a cookie's entire
 * "name=value; attributes" string must stay under the browser hard limit
 * (~4096 bytes). We store an ALREADY URL-ENCODED value, so we must chunk by the
 * *encoded* length — not the raw length. A previous implementation chunked raw
 * characters then encoded each chunk, which could roughly double a 3500-char
 * chunk past the 4 KB limit; the browser then silently dropped the oversized
 * chunk, truncating the session JSON and producing a malformed access token
 * (Storage: 403 "Invalid Compact JWS") that booted the user out.
 *
 * Why cookie wins on equal/fresher expiry: a sibling subdomain that refreshed
 * writes the new session to the shared cookie but not to this origin's
 * localStorage. Preferring a stale local refresh_token causes
 * "Invalid Refresh Token: Already Used" and a hard logout across apps.
 */
export const createCrossSubdomainAuthStorage = () => {
  if (typeof window === 'undefined') return undefined;
  const host = window.location.hostname;
  const shareCookie = host === 'rehub.studio' || host.endsWith('.rehub.studio');
  const CHUNK = 3000; // encoded bytes per chunk, leaving headroom for name + attrs
  const MAX_CHUNKS = 64;
  const MAX_AGE = 60 * 60 * 24 * 365;
  const cookieAttrs = '; Domain=.rehub.studio; Path=/; SameSite=Lax; Secure';

  const getRawC = (n: string): string | null => {
    const pref = `${n}=`;
    const parts = (document.cookie || '').split('; ');
    for (const c of parts) {
      if (c.startsWith(pref)) return c.slice(pref.length);
    }
    return null;
  };
  const setRawC = (n: string, encodedVal: string) => {
    document.cookie = `${n}=${encodedVal}${cookieAttrs}; Max-Age=${MAX_AGE}`;
  };
  const delC = (n: string) => {
    document.cookie = `${n}=${cookieAttrs}; Max-Age=0`;
  };

  const readAll = (k: string): string | null => {
    const single = getRawC(k);
    if (single !== null) {
      try { return decodeURIComponent(single); } catch { return null; }
    }
    const parts: string[] = [];
    for (let i = 0; i < MAX_CHUNKS; i++) {
      const p = getRawC(`${k}.${i}`);
      if (p === null) break;
      parts.push(p);
    }
    if (!parts.length) return null;
    try { return decodeURIComponent(parts.join('')); } catch { return null; }
  };

  const writeAll = (k: string, v: string) => {
    delC(k);
    for (let i = 0; i < MAX_CHUNKS; i++) delC(`${k}.${i}`);
    const encoded = encodeURIComponent(v);
    if (encoded.length <= CHUNK) { setRawC(k, encoded); return; }
    // Split the encoded string without ever cutting inside a "%XX" escape.
    let idx = 0;
    for (let pos = 0; pos < encoded.length; idx++) {
      let end = Math.min(pos + CHUNK, encoded.length);
      if (end < encoded.length) {
        if (encoded[end - 1] === '%') end -= 1;
        else if (encoded[end - 2] === '%') end -= 2;
      }
      setRawC(`${k}.${idx}`, encoded.slice(pos, end));
      pos = end;
    }
  };

  const deleteAll = (k: string) => {
    delC(k);
    for (let i = 0; i < MAX_CHUNKS; i++) delC(`${k}.${i}`);
  };

  const decodeStored = (v: string): unknown | null => {
    let json = v;
    if (v.startsWith('base64-')) {
      try { json = atob(v.slice(7)); } catch { return null; }
    }
    const t = json.trim();
    if (!t.startsWith('{')) return null;
    try { return JSON.parse(t); } catch { return null; }
  };

  /**
   * Accept only complete, parseable sessions (or non-session values like the
   * PKCE code-verifier). Reject truncated/corrupt session blobs that previously
   * passed a weak length check and then caused Invalid Compact JWS logouts.
   */
  const looksComplete = (v: string | null): v is string => {
    if (!v) return false;
    if (v.startsWith('base64-')) {
      // Must decode to parseable JSON. Do not require a JWT here — PKCE
      // intermediate writes and older supabase-js shapes would fail that
      // and get purged mid-login.
      return decodeStored(v) !== null;
    }
    const t = v.trim();
    if (t.startsWith('{')) {
      try { JSON.parse(t); return true; } catch { return false; }
    }
    // PKCE code-verifier and other non-JSON auth keys.
    return v.length > 0;
  };

  const getExpiry = (v: string | null): number => {
    if (!v) return 0;
    const parsed = decodeStored(v) as {
      expires_at?: unknown;
      currentSession?: { expires_at?: unknown };
    } | null;
    if (!parsed) return 0;
    const exp = parsed.expires_at ?? parsed.currentSession?.expires_at;
    return typeof exp === 'number' ? exp : 0;
  };

  const getRefreshToken = (v: string | null): string => {
    if (!v) return '';
    const parsed = decodeStored(v) as {
      refresh_token?: unknown;
      currentSession?: { refresh_token?: unknown };
    } | null;
    if (!parsed) return '';
    const token = parsed.refresh_token ?? parsed.currentSession?.refresh_token;
    return typeof token === 'string' ? token : '';
  };

  return {
    getItem: (k: string): string | null => {
      const local: string | null = (() => {
        try { return window.localStorage.getItem(k); } catch { return null; }
      })();
      const cookieVal: string | null = shareCookie ? readAll(k) : null;

      const localOk = looksComplete(local);
      const cookieOk = looksComplete(cookieVal);

      // PKCE verifier is written by THIS origin just before redirect. A
      // shared cookie from an older attempt on another subdomain will fail
      // the code exchange and hang /auth/callback until timeout.
      if (k.includes('code-verifier')) {
        if (localOk) return local;
        if (cookieOk) {
          try { window.localStorage.setItem(k, cookieVal); } catch { /* ignore */ }
          return cookieVal;
        }
        return null;
      }

      // Both valid: prefer the shared cookie only when it is STRICTLY newer
      // (sibling app rotated the refresh token). Equal expiry (including 0)
      // must keep local — otherwise we overwrite a just-written PKCE/session
      // with a stale cookie and login hangs.
      if (localOk && cookieOk) {
        const cookieExp = getExpiry(cookieVal);
        const localExp = getExpiry(local);
        const cookieRefresh = getRefreshToken(cookieVal);
        const localRefresh = getRefreshToken(local);
        const refreshDiverged =
          Boolean(cookieRefresh) &&
          Boolean(localRefresh) &&
          cookieRefresh !== localRefresh;

        if (cookieExp > localExp || (refreshDiverged && cookieExp >= localExp)) {
          try { window.localStorage.setItem(k, cookieVal); } catch { /* ignore */ }
          return cookieVal;
        }
        return local;
      }

      if (localOk) return local;

      if (cookieOk) {
        try { window.localStorage.setItem(k, cookieVal); } catch { /* ignore */ }
        return cookieVal;
      }

      // Truncated session JSON only — never delete a code-verifier, and never
      // wipe localStorage just because the cookie is junk.
      const looksLikeSessionKey = !k.includes('code-verifier') && !k.includes('user');
      if (looksLikeSessionKey && (local !== null || cookieVal !== null)) {
        if (local !== null && !localOk) {
          try { window.localStorage.removeItem(k); } catch { /* ignore */ }
        }
        if (shareCookie && cookieVal !== null && !cookieOk) {
          try { deleteAll(k); } catch { /* ignore */ }
        }
      }
      return null;
    },
    setItem: (k: string, v: string): void => {
      try { window.localStorage.setItem(k, v); } catch { /* ignore */ }
      if (shareCookie) { try { writeAll(k, v); } catch { /* ignore */ } }
    },
    removeItem: (k: string): void => {
      // Snapshot before wiping local — a sibling *.rehub.studio app may have
      // already rotated the shared cookie to a newer refresh token. If we
      // blindly deleteAll() here, one failed refresh on this origin boots
      // every other ReHub app that was still logged in.
      let local: string | null = null;
      try { local = window.localStorage.getItem(k); } catch { /* ignore */ }
      const cookieVal: string | null = shareCookie ? readAll(k) : null;

      try { window.localStorage.removeItem(k); } catch { /* ignore */ }
      if (!shareCookie) return;

      const localRefresh = getRefreshToken(local);
      const cookieRefresh = getRefreshToken(cookieVal);
      if (
        cookieRefresh &&
        localRefresh &&
        cookieRefresh !== localRefresh
      ) {
        // Cookie belongs to a sibling rotation — leave it alone.
        return;
      }
      if (getExpiry(cookieVal) > getExpiry(local)) {
        return;
      }
      try { deleteAll(k); } catch { /* ignore */ }
    },
  };
};
